import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRotate,
  faExclamationCircle,
  faExclamationTriangle,
  faSpinner,
  faArrowUp,
  faArrowDown,
  faTimes,
  faCog,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import { accountApi } from '../api/accountApi';
import { orderApi } from '../api/orderApi';
import { schwabApi } from '../api/schwabApi';
import { marketDataApi } from '../api/marketDataApi';
import { EarningsBadge } from '../contexts/EarningsBookContext';
import { NoteBadge } from '../contexts/SymbolNotesContext';
import { useRisk } from '../contexts/RiskContext';
import { useOrderPresets } from '../contexts/OrderPresetsContext';
import { useAssetNames } from '../hooks/useAssetNames';

const PositionsTable = forwardRef(({ onOrderExecuted, onSymbolSelect, venue = 'alpaca', schwabAccount = '' }, ref) => {
  const { checkOrder } = useRisk();
  const { activePreset, buildOrderFromPreset, presets, activeId, setActiveId } = useOrderPresets();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [closingAll, setClosingAll] = useState(false);
  const [confirmCloseAll, setConfirmCloseAll] = useState(false);
  const [closeMessage, setCloseMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState({}); // Track loading state for each position action
  const [actionMessage, setActionMessage] = useState({}); // Track messages for each position
  const [quickTradeEnabled, setQuickTradeEnabled] = useState(() => {
    const saved = localStorage.getItem('positionsQuickTradeEnabled');
    return saved === 'true';
  });
  const [quickTradeConfig, setQuickTradeConfig] = useState(() => {
    const saved = localStorage.getItem('positionsQuickTradeConfig');
    return saved ? JSON.parse(saved) : { buyQty: '', sellQty: '' };
  });
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configDraft, setConfigDraft] = useState(quickTradeConfig);
  const [configError, setConfigError] = useState('');
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [tradeModalState, setTradeModalState] = useState({
    symbol: '',
    side: 'buy',
    qty: '',
  });
  const [tradeOrderType, setTradeOrderType] = useState('market');
  const [tradeTimeInForce, setTradeTimeInForce] = useState('day');
  const [tradeLimitPrice, setTradeLimitPrice] = useState('');
  const [tradeStopPrice, setTradeStopPrice] = useState('');
  const [tradeModalLoading, setTradeModalLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  const loadPositions = async (showLoading = true) => {
    try {
      if (showLoading && positions.length === 0) setLoading(true);
      if (venue === 'schwab') {
        const response = await schwabApi.getPositions();
        let rows = response.data || [];
        if (schwabAccount) {
          rows = rows.filter((p) => String(p.accountNumber) === String(schwabAccount));
        }
        setPositions(rows);
      } else {
        const response = await accountApi.getPositions();
        setPositions(response.data || []);
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Expose refresh function to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: () => loadPositions(false), // Don't show loading spinner on manual refresh
  }));

  const notifyOrderExecuted = () => {
    if (typeof onOrderExecuted === 'function') {
      onOrderExecuted();
    }
  };

  useEffect(() => {
    setPositions([]);
    setLoading(true);
    loadPositions(true);
    const interval = setInterval(() => loadPositions(false), 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue, schwabAccount]);

  const formatCurrency = (value) => {
    const normalized = Number.isFinite(Number(value)) ? Number(value) : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(normalized);
  };

  const formatPercent = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    const pct = venue === 'schwab' && Math.abs(n) <= 1 ? n * 100 : n;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <FontAwesomeIcon icon={faSort} className="text-gray-400 text-[8px] ml-1" />;
    }
    return sortDirection === 'asc' 
      ? <FontAwesomeIcon icon={faSortUp} className="text-indigo-600 text-[8px] ml-1" />
      : <FontAwesomeIcon icon={faSortDown} className="text-indigo-600 text-[8px] ml-1" />;
  };

  const getTodayPl = (position) => {
    const qty = Math.abs(parseFloat(position.qty) || 0);
    const changePerShare = parseFloat(position.changeToday) || 0;
    const isShort = position.side?.toLowerCase() === 'short' || position.side?.toLowerCase() === 'sell';
    const direction = isShort ? -1 : 1;
    return qty * changePerShare * direction;
  };

  const positionSymbols = useMemo(
    () => positions.map((p) => p.symbol).filter(Boolean),
    [positions]
  );
  const assetNames = useAssetNames(positionSymbols);

  const sortedPositions = [...positions].sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aVal, bVal;
    switch (sortColumn) {
      case 'symbol':
        aVal = a.symbol?.toUpperCase() || '';
        bVal = b.symbol?.toUpperCase() || '';
        break;
      case 'side':
        aVal = a.side?.toUpperCase() || '';
        bVal = b.side?.toUpperCase() || '';
        break;
      case 'quantity':
        aVal = parseFloat(a.qty) || 0;
        bVal = parseFloat(b.qty) || 0;
        break;
      case 'avgPrice':
        aVal = parseFloat(a.costBasis / a.qty) || 0;
        bVal = parseFloat(b.costBasis / b.qty) || 0;
        break;
      case 'currentPrice':
        aVal = parseFloat(a.currentPrice) || 0;
        bVal = parseFloat(b.currentPrice) || 0;
        break;
      case 'marketValue':
        aVal = parseFloat(a.marketValue) || 0;
        bVal = parseFloat(b.marketValue) || 0;
        break;
      case 'unrealizedPl':
        aVal = parseFloat(a.unrealizedPl) || 0;
        bVal = parseFloat(b.unrealizedPl) || 0;
        break;
      case 'unrealizedPlpc':
        aVal = parseFloat(a.unrealizedPlpc) || 0;
        bVal = parseFloat(b.unrealizedPlpc) || 0;
        break;
      case 'changeToday':
        aVal = parseFloat(a.changeToday) || 0;
        bVal = parseFloat(b.changeToday) || 0;
        break;
      case 'todayPl':
        aVal = getTodayPl(a);
        bVal = getTodayPl(b);
        break;
      default:
        return 0;
    }
    
    if (typeof aVal === 'string') {
      return sortDirection === 'asc' 
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const totals = sortedPositions.reduce(
    (acc, position) => {
      const qtyAbs = Math.abs(parseFloat(position.qty) || 0);
      acc.totalQty += qtyAbs;
      acc.totalCostBasis += parseFloat(position.costBasis) || 0;
      acc.totalMarketValue += parseFloat(position.marketValue) || 0;
      acc.totalUnrealizedPl += parseFloat(position.unrealizedPl) || 0;
      acc.weightedChangeToday += (parseFloat(position.changeToday) || 0) * qtyAbs;
      acc.totalTodayPl += getTodayPl(position);
      return acc;
    },
    {
      totalQty: 0,
      totalCostBasis: 0,
      totalMarketValue: 0,
      totalUnrealizedPl: 0,
      weightedChangeToday: 0,
      totalTodayPl: 0,
    }
  );

  const totalsAvgPrice = totals.totalQty > 0 ? totals.totalCostBasis / totals.totalQty : 0;
  const totalsCurrentPrice = totals.totalQty > 0 ? totals.totalMarketValue / totals.totalQty : 0;
  const totalsUnrealizedPlPct =
    totals.totalCostBasis > 0 ? (totals.totalUnrealizedPl / totals.totalCostBasis) * 100 : 0;
  const totalsChangeToday = totals.totalQty > 0 ? totals.weightedChangeToday / totals.totalQty : 0;

  const handleCloseAllPositions = async () => {
    if (!confirmCloseAll) {
      setConfirmCloseAll(true);
      return;
    }

    try {
      setClosingAll(true);
      setCloseMessage(null);

      let response;
      if (venue === 'schwab') {
        const rows = positions.filter((p) => Math.abs(Number(p.qty)) > 0);
        for (const pos of rows) {
          const qtyAbs = Math.abs(Number(pos.qty) || 0);
          await schwabApi.placeOrder({
            accountNumber: String(pos.accountNumber),
            symbol: String(pos.symbol).toUpperCase(),
            instruction: isLongPosition(pos) ? 'SELL' : 'BUY_TO_COVER',
            quantity: qtyAbs,
            orderType: 'MARKET',
            duration: 'DAY',
            session: 'NORMAL',
            assetType: pos.assetType || 'EQUITY',
          });
        }
        response = { message: `Submitted market closes for ${rows.length} Schwab position${rows.length === 1 ? '' : 's'}.` };
      } else {
        response = await accountApi.closeAllPositions();
      }
      
      setCloseMessage({ 
        type: 'success', 
        text: response.message || 'All positions have been closed successfully!' 
      });
      
      setConfirmCloseAll(false);
      
      // Reload positions after closing
      await loadPositions();
      notifyOrderExecuted();
      
      // Clear message after 5 seconds
      setTimeout(() => setCloseMessage(null), 5000);
    } catch (error) {
      setCloseMessage({ 
        type: 'error', 
        text: error.response?.data?.error || error.message || 'Failed to close positions' 
      });
      setConfirmCloseAll(false);
    } finally {
      setClosingAll(false);
    }
  };

  const extractOrderError = (error) => {
    const data = error?.response?.data;
    if (data) {
      if (typeof data === 'string') return data;
      if (data.message) return data.message;
      if (data.error) return data.error;
      if (data.reason) return data.reason;
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        return data.errors
          .map((e) => (typeof e === 'string' ? e : e.message || JSON.stringify(e)))
          .join('; ');
      }
    }
    return error?.message || 'Request failed';
  };

  const isLongPosition = (pos) => {
    const side = String(pos?.side || '').toLowerCase();
    const qty = Number(pos?.qty) || 0;
    if (side === 'short' || side === 'sell') return false;
    if (side === 'long' || side === 'buy') return true;
    return qty >= 0;
  };

  const submitGuardedOrder = async (symbol, orderData, actionKey, successText) => {
    try {
      setActionLoading((prev) => ({ ...prev, [actionKey]: true }));
      setActionMessage((prev) => ({ ...prev, [symbol]: null }));

      const estimatedNotional =
        orderData.notional != null
          ? Number(orderData.notional)
          : orderData.limitPrice != null && orderData.qty != null
            ? Number(orderData.qty) * Number(orderData.limitPrice)
            : null;

      const guard = await guardOrder(orderData, estimatedNotional);
      if (!guard.ok) {
        if (guard.message) {
          setActionMessage((prev) => ({
            ...prev,
            [symbol]: { type: 'error', text: guard.message },
          }));
        }
        return false;
      }

      if (venue === 'schwab') {
        const pos = positions.find(
          (p) => String(p.symbol).toUpperCase() === String(symbol).toUpperCase()
        );
        const accountNumber = pos?.accountNumber || schwabAccount;
        if (!accountNumber) throw new Error('Select a Schwab account');
        const qty = Number(orderData.qty);
        if (!(qty > 0)) throw new Error('Schwab orders need a share quantity');
        const long = pos ? isLongPosition(pos) : orderData.side === 'buy';
        let instruction = orderData.side === 'buy' ? 'BUY' : 'SELL';
        if (!long) instruction = orderData.side === 'buy' ? 'BUY_TO_COVER' : 'SELL_SHORT';
        const orderType = String(orderData.type || 'market').toUpperCase();
        await schwabApi.placeOrder({
          accountNumber: String(accountNumber),
          symbol: String(symbol).toUpperCase(),
          instruction,
          quantity: qty,
          orderType: orderType === 'MARKET' ? 'MARKET' : orderType === 'STOP' ? 'STOP' : orderType.includes('STOP') ? 'STOP_LIMIT' : 'LIMIT',
          price: orderData.limitPrice,
          stopPrice: orderData.stopPrice,
          duration: 'DAY',
          session: 'NORMAL',
          assetType: pos?.assetType || 'EQUITY',
        });
      } else {
        await orderApi.submitOrder(orderData);
      }
      setActionMessage((prev) => ({
        ...prev,
        [symbol]: { type: 'success', text: successText },
      }));
      await loadPositions();
      notifyOrderExecuted();
      setTimeout(() => {
        setActionMessage((prev) => {
          const next = { ...prev };
          delete next[symbol];
          return next;
        });
      }, 6000);
      return true;
    } catch (error) {
      setActionMessage((prev) => ({
        ...prev,
        [symbol]: {
          type: 'error',
          text: extractOrderError(error),
        },
      }));
      return false;
    } finally {
      setActionLoading((prev) => {
        const next = { ...prev };
        delete next[actionKey];
        return next;
      });
    }
  };

  const handleFlatten = async (symbol, posRow = null) => {
    const actionKey = `flatten-${symbol}`;
    try {
      setActionLoading((prev) => ({ ...prev, [actionKey]: true }));
      setActionMessage((prev) => ({ ...prev, [symbol]: null }));
      let response;
      if (venue === 'schwab') {
        const pos =
          posRow ||
          positions.find(
            (p) => String(p.symbol).toUpperCase() === String(symbol).toUpperCase()
          );
        if (!pos?.accountNumber) throw new Error('Schwab account missing on position');
        const qtyAbs = Math.abs(Number(pos.qty) || 0);
        await schwabApi.placeOrder({
          accountNumber: String(pos.accountNumber),
          symbol: String(symbol).toUpperCase(),
          instruction: isLongPosition(pos) ? 'SELL' : 'BUY_TO_COVER',
          quantity: qtyAbs,
          orderType: 'MARKET',
          duration: 'DAY',
          session: 'NORMAL',
          assetType: pos.assetType || 'EQUITY',
        });
        response = { message: `Flattened ${symbol} on Schwab` };
      } else {
        response = await accountApi.closePosition(symbol);
      }
      setActionMessage((prev) => ({
        ...prev,
        [symbol]: {
          type: 'success',
          text: response.message || `Flattened ${symbol}`,
        },
      }));
      await loadPositions();
      notifyOrderExecuted();
      setTimeout(() => {
        setActionMessage((prev) => {
          const next = { ...prev };
          delete next[symbol];
          return next;
        });
      }, 6000);
    } catch (error) {
      setActionMessage((prev) => ({
        ...prev,
        [symbol]: {
          type: 'error',
          text: `Flatten failed: ${extractOrderError(error)}`,
        },
      }));
    } finally {
      setActionLoading((prev) => {
        const next = { ...prev };
        delete next[actionKey];
        return next;
      });
    }
  };

  const handleAdd = async (pos) => {
    const symbol = pos.symbol;
    const actionKey = `add-${symbol}`;
    const long = isLongPosition(pos);
    const side = long ? 'buy' : 'sell';
    const addQty =
      activePreset?.orderBy === 'qty' && activePreset?.qty
        ? Number(activePreset.qty)
        : Math.abs(Number(pos.qty)) || 1;

    try {
      let quote = null;
      if (activePreset?.orderType === 'limit' || activePreset?.orderType === 'stop_limit') {
        const qRes = await marketDataApi.getQuote(symbol);
        quote = qRes.data || qRes;
      }
      const orderData = buildOrderFromPreset(activePreset || { orderType: 'market', timeInForce: 'day', orderBy: 'qty', qty: addQty }, {
        symbol,
        side,
        qty: addQty,
        quote,
        currentPrice: pos.currentPrice,
      });
      await submitGuardedOrder(
        symbol,
        orderData,
        actionKey,
        `Added to ${symbol} (${side} ${orderData.qty || orderData.notional})`
      );
    } catch (error) {
      setActionMessage((prev) => ({
        ...prev,
        [symbol]: { type: 'error', text: error.message || 'Add failed' },
      }));
    }
  };

  const handleReverse = async (pos) => {
    const symbol = pos.symbol;
    const actionKey = `reverse-${symbol}`;
    const long = isLongPosition(pos);
    const absQty = Math.abs(Number(pos.qty)) || 0;
    if (!absQty) return;

    const reverseQty = absQty * 2;
    const side = long ? 'sell' : 'buy';
    const proceed = window.confirm(
      `Reverse ${symbol}? This ${side}s ${reverseQty} shares to flatten and open the opposite side.`
    );
    if (!proceed) return;

    try {
      // Reverse is safest as market; still honor TIF/ext hours from preset
      const preset = {
        ...(activePreset || {}),
        orderType: 'market',
        orderBy: 'qty',
        qty: reverseQty,
        limitOffset: null,
      };
      const orderData = buildOrderFromPreset(preset, {
        symbol,
        side,
        qty: reverseQty,
        currentPrice: pos.currentPrice,
      });
      await submitGuardedOrder(
        symbol,
        orderData,
        actionKey,
        `Reversed ${symbol} (${side} ${reverseQty})`
      );
    } catch (error) {
      setActionMessage((prev) => ({
        ...prev,
        [symbol]: { type: 'error', text: error.message || 'Reverse failed' },
      }));
    }
  };

  const guardOrder = async (order, estimatedNotional = null) => {
    const risk = await checkOrder({
      ...order,
      estimatedNotional,
    });
    if (risk.blocks?.length) {
      return { ok: false, message: `Risk block: ${risk.blocks.join(' · ')}` };
    }
    if (risk.warnings?.length) {
      const proceed = window.confirm(
        `Risk warnings:\n\n• ${risk.warnings.join('\n• ')}\n\nSubmit order anyway?`
      );
      if (!proceed) return { ok: false, message: null };
    }
    return { ok: true };
  };

  const handleBuyPosition = async (symbol, currentQty) => {
    const actionKey = `buy-${symbol}`;
    const qty = Math.abs(parseFloat(currentQty));
    
    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));
      setActionMessage(prev => ({ ...prev, [symbol]: null }));

      const orderData = {
        symbol: symbol.toUpperCase(),
        qty: qty,
        side: 'buy',
        type: 'market',
        timeInForce: 'day',
      };
      const pos = positions.find((p) => p.symbol === symbol);
      const estimatedNotional =
        pos?.currentPrice != null ? qty * Number(pos.currentPrice) : null;
      const guard = await guardOrder(orderData, estimatedNotional);
      if (!guard.ok) {
        if (guard.message) {
          setActionMessage((prev) => ({
            ...prev,
            [symbol]: { type: 'error', text: guard.message },
          }));
        }
        return;
      }

      const response = await orderApi.submitOrder(orderData);
      
      setActionMessage(prev => ({
        ...prev,
        [symbol]: { type: 'success', text: `Buy order for ${qty} shares of ${symbol} submitted successfully!` }
      }));
      
      // Reload positions after order
      await loadPositions();
      notifyOrderExecuted();
      
      // Clear message after a few seconds
      setTimeout(() => {
        setActionMessage(prev => {
          const newMsg = { ...prev };
          delete newMsg[symbol];
          return newMsg;
        });
      }, 8000);
    } catch (error) {
      setActionMessage(prev => ({
        ...prev,
        [symbol]: {
          type: 'error',
          text: `Failed to submit buy order: ${extractOrderError(error)}`,
        }
      }));
    } finally {
      setActionLoading(prev => {
        const newLoading = { ...prev };
        delete newLoading[actionKey];
        return newLoading;
      });
    }
  };

  const handleSellPosition = async (symbol, currentQty) => {
    const actionKey = `sell-${symbol}`;
    const qty = Math.abs(parseFloat(currentQty));
    
    try {
      setActionLoading(prev => ({ ...prev, [actionKey]: true }));
      setActionMessage(prev => ({ ...prev, [symbol]: null }));

      const orderData = {
        symbol: symbol.toUpperCase(),
        qty: qty,
        side: 'sell',
        type: 'market',
        timeInForce: 'day',
      };
      const pos = positions.find((p) => p.symbol === symbol);
      const estimatedNotional =
        pos?.currentPrice != null ? qty * Number(pos.currentPrice) : null;
      const guard = await guardOrder(orderData, estimatedNotional);
      if (!guard.ok) {
        if (guard.message) {
          setActionMessage((prev) => ({
            ...prev,
            [symbol]: { type: 'error', text: guard.message },
          }));
        }
        return;
      }

      const response = await orderApi.submitOrder(orderData);
      
      setActionMessage(prev => ({
        ...prev,
        [symbol]: { type: 'success', text: `Sell order for ${qty} shares of ${symbol} submitted successfully!` }
      }));
      
      // Reload positions after order
      await loadPositions();
      notifyOrderExecuted();
      
      // Clear message after a few seconds
      setTimeout(() => {
        setActionMessage(prev => {
          const newMsg = { ...prev };
          delete newMsg[symbol];
          return newMsg;
        });
      }, 8000);
    } catch (error) {
      setActionMessage(prev => ({
        ...prev,
        [symbol]: {
          type: 'error',
          text: `Failed to submit sell order: ${extractOrderError(error)}`,
        }
      }));
    } finally {
      setActionLoading(prev => {
        const newLoading = { ...prev };
        delete newLoading[actionKey];
        return newLoading;
      });
    }
  };

  const handleBuyClick = (symbol, currentQty) => {
    if (quickTradeEnabled) {
      const cfgQty = parseFloat(quickTradeConfig.buyQty);
      const qtyToUse =
        !Number.isNaN(cfgQty) && cfgQty > 0 ? cfgQty : Math.abs(parseFloat(currentQty));
      return handleBuyPosition(symbol, qtyToUse);
    }
    // Open trade modal for manual configuration
    setTradeModalState({
      symbol,
      side: 'buy',
      qty: Math.abs(parseFloat(currentQty)).toString(),
    });
    setTradeOrderType('market');
    setTradeTimeInForce('day');
    setTradeLimitPrice('');
    setTradeStopPrice('');
    setTradeModalLoading(false);
    setShowTradeModal(true);
  };

  const handleSellClick = (symbol, currentQty) => {
    if (quickTradeEnabled) {
      const cfgQty = parseFloat(quickTradeConfig.sellQty);
      const qtyToUse =
        !Number.isNaN(cfgQty) && cfgQty > 0 ? cfgQty : Math.abs(parseFloat(currentQty));
      return handleSellPosition(symbol, qtyToUse);
    }
    // Open trade modal for manual configuration
    setTradeModalState({
      symbol,
      side: 'sell',
      qty: Math.abs(parseFloat(currentQty)).toString(),
    });
    setTradeOrderType('market');
    setTradeTimeInForce('day');
    setTradeLimitPrice('');
    setTradeStopPrice('');
    setTradeModalLoading(false);
    setShowTradeModal(true);
  };

  const handleTradeModalSubmit = async (e) => {
    e.preventDefault();
    const qtyNum = parseFloat(tradeModalState.qty);
    if (Number.isNaN(qtyNum) || qtyNum <= 0) {
      return;
    }

    // Validate prices for non-market orders
    if ((tradeOrderType === 'limit' || tradeOrderType === 'stop_limit') && !tradeLimitPrice) {
      return;
    }
    if ((tradeOrderType === 'stop' || tradeOrderType === 'stop_limit') && !tradeStopPrice) {
      return;
    }

    setTradeModalLoading(true);
    try {
      const symbol = tradeModalState.symbol;
      const side = tradeModalState.side;
      const actionKey = `${side}-${symbol}-modal`;

      setActionLoading((prev) => ({ ...prev, [actionKey]: true }));
      setActionMessage((prev) => ({ ...prev, [symbol]: null }));

      const orderData = {
        symbol: symbol.toUpperCase(),
        qty: qtyNum,
        side,
        type: tradeOrderType,
        timeInForce: tradeTimeInForce,
      };

      if (tradeLimitPrice) {
        orderData.limitPrice = parseFloat(tradeLimitPrice);
      }
      if (tradeStopPrice) {
        orderData.stopPrice = parseFloat(tradeStopPrice);
      }

      const pos = positions.find((p) => p.symbol === symbol);
      let estimatedNotional = null;
      if (tradeOrderType === 'limit' && tradeLimitPrice) {
        estimatedNotional = qtyNum * parseFloat(tradeLimitPrice);
      } else if (pos?.currentPrice != null) {
        estimatedNotional = qtyNum * Number(pos.currentPrice);
      }
      const guard = await guardOrder(orderData, estimatedNotional);
      if (!guard.ok) {
        if (guard.message) {
          setActionMessage((prev) => ({
            ...prev,
            [symbol]: { type: 'error', text: guard.message },
          }));
        }
        return;
      }

      const response = await orderApi.submitOrder(orderData);

      setActionMessage((prev) => ({
        ...prev,
        [symbol]: {
          type: 'success',
          text: `Order (${tradeOrderType}) for ${qtyNum} shares of ${symbol} submitted successfully!`,
        },
      }));

      await loadPositions();
      notifyOrderExecuted();

      setTimeout(() => {
        setActionMessage((prev) => {
          const newMsg = { ...prev };
          delete newMsg[symbol];
          return newMsg;
        });
      }, 3000);

      setShowTradeModal(false);
    } finally {
      setTradeModalLoading(false);
      setActionLoading((prev) => {
        const copy = { ...prev };
        Object.keys(copy)
          .filter((k) => k.endsWith('-modal'))
          .forEach((k) => {
            delete copy[k];
          });
        return copy;
      });
    }
  };

  if (loading && positions.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/4 mb-3"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
        <p className="text-red-800 text-xs">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-gray-800">
          Open Positions ({positions.length})
          <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            {venue === 'schwab' ? 'Schwab' : 'Alpaca'}
          </span>
        </h2>
        <div className="flex items-center space-x-2">
          {/* One-click preset selector */}
          <div className="flex items-center gap-1 mr-1">
            <select
              value={activeId || ''}
              onChange={(e) => setActiveId(e.target.value)}
              className="h-7 px-1.5 text-[10px] border border-gray-300 rounded bg-white text-gray-700 max-w-[140px]"
              title="Active order preset for Add"
            >
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                setConfigDraft(quickTradeConfig);
                setConfigError('');
                setShowConfigModal(true);
              }}
              className="p-1 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
              title="Legacy quick-trade qty overrides"
            >
              <FontAwesomeIcon icon={faCog} className="text-xs" />
            </button>
          </div>
          {positions.length > 0 && (
            <>
              {!confirmCloseAll ? (
                <button
                  onClick={handleCloseAllPositions}
                  disabled={closingAll}
                  className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-xs" />
                  Close All
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <div className="bg-red-50 border-2 border-red-500 rounded-lg px-4 py-2 flex items-center space-x-3">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
                    <span className="text-red-800 font-semibold text-sm">Close all?</span>
                    <button
                      onClick={handleCloseAllPositions}
                      disabled={closingAll}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {closingAll ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                          Closing...
                        </>
                      ) : (
                        'Yes'
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setConfirmCloseAll(false);
                        setCloseMessage(null);
                      }}
                      disabled={closingAll}
                      className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm font-semibold disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          <button
            onClick={loadPositions}
            disabled={closingAll}
            className="px-3 py-1 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faRotate} className="mr-1 text-xs" />
            Refresh
          </button>
        </div>
      </div>

      {closeMessage && (
        <div className={`mb-4 p-4 rounded-lg ${
          closeMessage.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {closeMessage.text}
        </div>
      )}

      {positions.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No open positions
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('symbol')}
                >
                  <div className="flex items-center">
                    Symbol
                    {getSortIcon('symbol')}
                  </div>
                </th>
                <th 
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('side')}
                >
                  <div className="flex items-center">
                    Side
                    {getSortIcon('side')}
                  </div>
                </th>
                <th 
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('quantity')}
                >
                  <div className="flex items-center">
                    Quantity
                    {getSortIcon('quantity')}
                  </div>
                </th>
                <th 
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('avgPrice')}
                >
                  <div className="flex items-center">
                    Avg Price
                    {getSortIcon('avgPrice')}
                  </div>
                </th>
                <th 
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('currentPrice')}
                >
                  <div className="flex items-center">
                    Current Price
                    {getSortIcon('currentPrice')}
                  </div>
                </th>
                <th 
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('marketValue')}
                >
                  <div className="flex items-center">
                    Market Value
                    {getSortIcon('marketValue')}
                  </div>
                </th>
                <th 
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('unrealizedPl')}
                >
                  <div className="flex items-center">
                    Unrealized P/L
                    {getSortIcon('unrealizedPl')}
                  </div>
                </th>
                <th 
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('unrealizedPlpc')}
                >
                  <div className="flex items-center">
                    P/L %
                    {getSortIcon('unrealizedPlpc')}
                  </div>
                </th>
                <th 
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('changeToday')}
                >
                  <div className="flex items-center">
                    Change Today
                    {getSortIcon('changeToday')}
                  </div>
                </th>
                <th
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('todayPl')}
                >
                  <div className="flex items-center">
                    Today P/L
                    {getSortIcon('todayPl')}
                  </div>
                </th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedPositions.map((pos, index) => {
                const symbol = pos.symbol;
                const isLoading =
                  actionLoading[`add-${symbol}`] ||
                  actionLoading[`reverse-${symbol}`] ||
                  actionLoading[`flatten-${symbol}`] ||
                  actionLoading[`buy-${symbol}`] ||
                  actionLoading[`sell-${symbol}`] ||
                  actionLoading[`close-${symbol}`];
                const message = actionMessage[symbol];
                const companyName = assetNames[String(symbol || '').toUpperCase()] || '';
                const symbolTitle = companyName ? `${symbol} — ${companyName}` : symbol;
                
                return (
                  <React.Fragment key={`${pos.symbol}-${index}`}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-2 py-1.5 font-bold">
                        <div className="inline-flex items-center gap-1.5 flex-wrap">
                          {onSymbolSelect ? (
                            <button
                              type="button"
                              onClick={() => onSymbolSelect(pos.symbol)}
                              className="text-indigo-600 hover:text-indigo-800 hover:underline transition-colors"
                              title={symbolTitle}
                              aria-label={symbolTitle}
                            >
                              {pos.symbol}
                            </button>
                          ) : (
                            <Link
                              to={`/asset/${encodeURIComponent(pos.symbol)}`}
                              className="text-indigo-600 hover:text-indigo-800 hover:underline transition-colors"
                              title={symbolTitle}
                              aria-label={symbolTitle}
                            >
                              {pos.symbol}
                            </Link>
                          )}
                          {venue === 'schwab' && pos.accountNumber && !schwabAccount && (
                            <span className="text-[9px] text-gray-400">
                              ···{String(pos.accountNumber).slice(-4)}
                            </span>
                          )}
                          <EarningsBadge symbol={pos.symbol} />
                          <NoteBadge symbol={pos.symbol} />
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          pos.side.toLowerCase() === 'long' || pos.side.toLowerCase() === 'buy'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {pos.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-gray-700">{pos.qty.toFixed(4)}</td>
                      <td className="px-2 py-1.5 text-gray-700">{formatCurrency(pos.costBasis / pos.qty)}</td>
                      <td className="px-2 py-1.5 text-gray-700">{formatCurrency(pos.currentPrice)}</td>
                      <td className="px-2 py-1.5 text-gray-700">{formatCurrency(pos.marketValue)}</td>
                      <td className={`px-2 py-1.5 font-semibold ${pos.unrealizedPl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(pos.unrealizedPl)}
                      </td>
                      <td className={`px-2 py-1.5 font-semibold ${pos.unrealizedPlpc >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercent(pos.unrealizedPlpc)}
                      </td>
                      <td className={`px-2 py-1.5 font-medium ${pos.changeToday >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(pos.changeToday)}
                      </td>
                      <td className={`px-2 py-1.5 font-semibold ${getTodayPl(pos) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(getTodayPl(pos))}
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleAdd(pos)}
                            disabled={isLoading}
                            className="px-2 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-[10px] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-0.5"
                            title={`Add using preset (${activePreset?.name || 'default'})`}
                          >
                            {actionLoading[`add-${symbol}`] ? (
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[10px]" />
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faArrowUp} className="text-[10px]" />
                                <span>Add</span>
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleFlatten(symbol, pos)}
                            disabled={isLoading}
                            className="px-2 py-0.5 bg-slate-700 text-white rounded hover:bg-slate-800 transition-colors text-[10px] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-0.5"
                            title="Flatten (close) entire position"
                          >
                            {actionLoading[`flatten-${symbol}`] ? (
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[10px]" />
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                                <span>Flat</span>
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleReverse(pos)}
                            disabled={isLoading}
                            className="px-2 py-0.5 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors text-[10px] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-0.5"
                            title="Reverse: flatten and open opposite size"
                          >
                            {actionLoading[`reverse-${symbol}`] ? (
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[10px]" />
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faArrowDown} className="text-[10px]" />
                                <span>Rev</span>
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {message && (
                      <tr>
                        <td colSpan="11" className="px-4 py-2">
                          <div className={`p-2 rounded text-xs ${
                            message.type === 'success'
                              ? 'bg-green-50 border border-green-200 text-green-800'
                              : 'bg-red-50 border border-red-200 text-red-800'
                          }`}>
                            {message.text}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              <tr className="bg-gray-50 border-t-2 border-gray-200">
                <td className="px-2 py-2 font-bold text-gray-800">TOTAL</td>
                <td className="px-2 py-2 text-gray-600 font-semibold">MIXED</td>
                <td className="px-2 py-2 font-semibold text-gray-800">{totals.totalQty.toFixed(4)}</td>
                <td className="px-2 py-2 font-semibold text-gray-800">{formatCurrency(totalsAvgPrice)}</td>
                <td className="px-2 py-2 font-semibold text-gray-800">{formatCurrency(totalsCurrentPrice)}</td>
                <td className="px-2 py-2 font-semibold text-gray-800">{formatCurrency(totals.totalMarketValue)}</td>
                <td className={`px-2 py-2 font-bold ${totals.totalUnrealizedPl >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(totals.totalUnrealizedPl)}
                </td>
                <td className={`px-2 py-2 font-bold ${totalsUnrealizedPlPct >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatPercent(totalsUnrealizedPlPct)}
                </td>
                <td className={`px-2 py-2 font-semibold ${totalsChangeToday >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(totalsChangeToday)}
                </td>
                <td className={`px-2 py-2 font-bold ${totals.totalTodayPl >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(totals.totalTodayPl)}
                </td>
                <td className="px-2 py-2 text-[10px] text-gray-500 uppercase tracking-wide">
                  —
                </td>
              </tr>
              <tr className="bg-gray-50">
                <td colSpan="9" className="px-2 pb-2 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
                  Total Today P/L
                </td>
                <td className={`px-2 pb-2 font-bold ${totals.totalTodayPl >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(totals.totalTodayPl)}
                </td>
                <td className="px-2 pb-2" />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Quick trade config modal */}
      {showConfigModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                <FontAwesomeIcon icon={faCog} className="mr-2 text-gray-600" />
                Quick Market Trade Settings
              </h3>
              <button
                type="button"
                onClick={() => setShowConfigModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              These quantities are used when <span className="font-semibold">Quick market</span> is
              enabled and you click Buy/Sell. Leave blank to use the full position size.
            </p>
            {configError && (
              <div className="mb-2 p-2 text-xs rounded bg-red-50 border border-red-200 text-red-700">
                {configError}
              </div>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Quick Buy quantity (shares)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={configDraft.buyQty}
                  onChange={(e) =>
                    setConfigDraft((prev) => ({ ...prev, buyQty: e.target.value }))
                  }
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g. 10 (blank = full position)"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Quick Sell quantity (shares)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={configDraft.sellQty}
                  onChange={(e) =>
                    setConfigDraft((prev) => ({ ...prev, sellQty: e.target.value }))
                  }
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g. 10 (blank = full position)"
                />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end space-x-2">
              <button
                type="button"
                onClick={() => setShowConfigModal(false)}
                className="px-3 py-1.5 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  // Simple validation: non-negative numbers or blank
                  const buy = configDraft.buyQty.trim();
                  const sell = configDraft.sellQty.trim();
                  if (
                    (buy && (Number.isNaN(parseFloat(buy)) || parseFloat(buy) < 0)) ||
                    (sell && (Number.isNaN(parseFloat(sell)) || parseFloat(sell) < 0))
                  ) {
                    setConfigError('Quantities must be non-negative numbers or blank.');
                    return;
                  }
                  const newCfg = { buyQty: buy, sellQty: sell };
                  setQuickTradeConfig(newCfg);
                  localStorage.setItem('positionsQuickTradeConfig', JSON.stringify(newCfg));
                  setConfigError('');
                  setShowConfigModal(false);
                }}
                className="px-3 py-1.5 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trade confirmation modal when quick mode is off */}
      {showTradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">
                {tradeModalState.side === 'buy' ? 'Buy Position' : 'Sell Position'} –{' '}
                <span className="text-indigo-600 font-bold">{tradeModalState.symbol}</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowTradeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              This will place a{' '}
              <span className="font-semibold">
                {tradeOrderType.toUpperCase()} {tradeModalState.side}
              </span>{' '}
              order with the settings below.
            </p>
            <form onSubmit={handleTradeModalSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Order Type</label>
                  <select
                    value={tradeOrderType}
                    onChange={(e) => setTradeOrderType(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="market">Market</option>
                    <option value="limit">Limit</option>
                    <option value="stop">Stop</option>
                    <option value="stop_limit">Stop Limit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Time in Force</label>
                  <select
                    value={tradeTimeInForce}
                    onChange={(e) => setTradeTimeInForce(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="day">DAY</option>
                    <option value="gtc">GTC</option>
                    <option value="opg">OPG</option>
                    <option value="cls">CLS</option>
                    <option value="ioc">IOC</option>
                    <option value="fok">FOK</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={tradeModalState.qty}
                  onChange={(e) =>
                    setTradeModalState((prev) => ({ ...prev, qty: e.target.value }))
                  }
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Quantity in shares"
                />
              </div>
              {(tradeOrderType === 'limit' || tradeOrderType === 'stop_limit') && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Limit Price
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    value={tradeLimitPrice}
                    onChange={(e) => setTradeLimitPrice(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g. 150.25"
                  />
                </div>
              )}
              {(tradeOrderType === 'stop' || tradeOrderType === 'stop_limit') && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Stop Price
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    value={tradeStopPrice}
                    onChange={(e) => setTradeStopPrice(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g. 145.00"
                  />
                </div>
              )}
              <div className="flex items-center justify-end space-x-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowTradeModal(false)}
                  className="px-3 py-1.5 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  disabled={tradeModalLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={tradeModalLoading}
                  className="px-3 py-1.5 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {tradeModalLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Order'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
});

PositionsTable.displayName = 'PositionsTable';

export default PositionsTable;
